import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';

type Template = { id: string; name: string; kind: string };
export type Segment = { id: string; name: string; kind: string; memberCount: number };

export function SegmentPicker({
  segments,
  selected,
  title,
  onToggle,
}: {
  segments: Segment[];
  selected: string[];
  title: string;
  onToggle: (id: string) => void;
}) {
  return (
    <fieldset className="min-w-0 space-y-2">
      <legend className="text-sm font-medium">{title}</legend>
      <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border p-2">
        {segments.map((s) => (
          <label
            key={s.id}
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-base leading-6 transition-colors ${selected.includes(s.id) ? 'border-blue-200 bg-[var(--ff-primary-subtle)] text-[var(--ff-primary)]' : 'border-transparent hover:bg-muted'}`}
          >
            <input
              type="checkbox"
              checked={selected.includes(s.id)}
              onChange={() => onToggle(s.id)}
              className="mt-1 size-4 shrink-0 cursor-pointer accent-[var(--ff-primary)]"
            />
            <span className="min-w-0 break-words">
              {s.name}{' '}
              <span className="whitespace-nowrap font-medium">
                ({s.memberCount.toLocaleString()} {s.memberCount === 1 ? 'member' : 'members'})
              </span>
            </span>
          </label>
        ))}
        {segments.length === 0 && (
          <p className="p-2 text-sm text-muted-foreground">No segments available.</p>
        )}
      </div>
    </fieldset>
  );
}

export function CampaignForm({
  templates,
  segments,
  initial,
}: {
  templates: Template[];
  segments: Segment[];
  initial?: {
    id: string;
    name: string;
    templateId: string;
    segmentIds: string[];
    excludeSegmentIds: string[];
    fromName: string;
    fromAddress: string;
    replyTo: string | null;
  };
}) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    templateId: initial?.templateId ?? templates[0]?.id ?? '',
    segmentIds: initial?.segmentIds ?? [],
    excludeSegmentIds: initial?.excludeSegmentIds ?? [],
    fromName: initial?.fromName ?? 'Facet Flow',
    fromAddress: initial?.fromAddress ?? '',
    replyTo: initial?.replyTo ?? 'contact@facetflow.io',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (key: 'segmentIds' | 'excludeSegmentIds', id: string) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
    }));

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        ...form,
        fromAddress: form.fromAddress || undefined,
        replyTo: form.replyTo || undefined,
      };
      if (initial) {
        const details = {
          name: body.name,
          templateId: body.templateId,
          fromName: body.fromName,
          fromAddress: body.fromAddress,
          replyTo: body.replyTo,
        };
        await api(`/campaigns/${initial.id}`, { method: 'PATCH', json: details });
        revalidate();
      } else {
        const c = await api<{ id: string }>('/campaigns', { method: 'POST', json: body });
        navigate(`/campaigns/${c.id}`);
      }
    } catch (err) {
      setError(errMsg(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`grid gap-6 rounded-lg border bg-white p-5 text-sm ${initial ? '' : 'xl:grid-cols-2'}`}
    >
      <div className="min-w-0 space-y-4">
        <h2 className="text-lg">Campaign details</h2>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Name</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="h-9 w-full rounded-md border px-2"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Template</span>
          <select
            value={form.templateId}
            onChange={(e) => setForm({ ...form, templateId: e.target.value })}
            className="h-9 w-full rounded-md border px-2"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.kind})
              </option>
            ))}
          </select>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">From name</span>
            <input
              value={form.fromName}
              onChange={(e) => setForm({ ...form, fromName: e.target.value })}
              className="h-9 w-full rounded-md border px-2"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">From address (default: env)</span>
            <input
              value={form.fromAddress}
              onChange={(e) => setForm({ ...form, fromAddress: e.target.value })}
              className="h-9 w-full rounded-md border px-2"
              placeholder="hello@news.facetflow.ai"
            />
          </label>
        </div>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Reply-to</span>
          <input
            value={form.replyTo}
            onChange={(e) => setForm({ ...form, replyTo: e.target.value })}
            className="h-9 w-full rounded-md border px-2"
          />
        </label>
        <Button
          onClick={save}
          disabled={
            busy || !form.name || !form.templateId || (!initial && form.segmentIds.length === 0)
          }
        >
          {initial ? 'Save changes' : 'Create draft'}
        </Button>
        {initial && (
          <p className="text-sm text-muted-foreground">
            Save details before sending. Preview and test emails are optional.
          </p>
        )}
        {error && (
          <p role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-800">
            {error}
          </p>
        )}
      </div>
      {!initial && (
        <div className="min-w-0 space-y-4">
          <h2 className="text-lg">Audience</h2>
          <p className="text-sm text-muted-foreground">
            Counts show segment members. Overlapping members are counted once; recipient exclusions
            are calculated automatically after creating the draft.
          </p>
          <SegmentPicker
            segments={segments}
            selected={form.segmentIds}
            onToggle={(id) => toggle('segmentIds', id)}
            title="Send to (union of segments)"
          />
          <SegmentPicker
            segments={segments}
            selected={form.excludeSegmentIds}
            onToggle={(id) => toggle('excludeSegmentIds', id)}
            title="Exclude"
          />
        </div>
      )}
    </div>
  );
}
